import { useState, useEffect, useCallback } from 'react'
import { api } from '../../lib/api'
import { toast } from '../ui/Toast'
import LoadingSpinner from '../ui/LoadingSpinner'

const SPORTS = [
  { key: 'nfl', label: 'NFL' },
  { key: 'nba', label: 'NBA' },
  { key: 'wnba', label: 'WNBA' },
  { key: 'mlb', label: 'MLB' },
]

const POSITIONS_BY_SPORT = {
  nfl: ['all', 'QB', 'RB', 'WR', 'TE', 'K', 'DEF', 'DL', 'LB', 'DB', 'S'],
  nba: ['all', 'PG', 'SG', 'SF', 'PF', 'C', 'G', 'F'],
  wnba: ['all', 'G', 'F', 'C'],
  mlb: ['all', 'SP', 'RP', 'C', '1B', '2B', '3B', 'SS', 'OF', 'DH'],
}

// Injury filter chips. 'Any' matches any non-null injury_status. Specific
// statuses match exactly. NFL most commonly returns Questionable / Doubtful /
// Probable / Out / IR. DTD is rare for NFL (more common in NBA/MLB) but
// included for completeness since Sleeper occasionally surfaces it.
const INJURY_FILTERS = [
  { key: 'all', label: 'All' },
  { key: 'any', label: 'Any injury' },
  { key: 'Out', label: 'Out' },
  { key: 'IR', label: 'IR' },
  { key: 'Doubtful', label: 'Doubtful' },
  { key: 'Questionable', label: 'Questionable' },
  { key: 'Probable', label: 'Probable' },
  { key: 'Day-To-Day', label: 'DTD' },
]

export default function PlayerBlurbsPanel() {
  const [sport, setSport] = useState('nfl')
  const [syncingEspn, setSyncingEspn] = useState(false)
  const [position, setPosition] = useState('all')
  const [players, setPlayers] = useState([])
  const [loading, setLoading] = useState(false)
  const [selected, setSelected] = useState(new Set())
  const [generating, setGenerating] = useState(false)
  const [publishing, setPublishing] = useState(false)
  const [publishingEspn, setPublishingEspn] = useState(false)
  const [editingId, setEditingId] = useState(null)
  const [editContent, setEditContent] = useState('')
  const [creatingFor, setCreatingFor] = useState(null)
  const [createContent, setCreateContent] = useState('')
  const [historyPlayerId, setHistoryPlayerId] = useState(null)
  const [history, setHistory] = useState([])
  // Tap-to-preview the current blurb inline without opening the Edit
  // textarea. Cuts a step for the common "just check what the note says"
  // scan. Only active when the player has a blurb.
  const [expandedId, setExpandedId] = useState(null)
  // Blurbs are no longer tagged by week. The field defaulted to 1 and was
  // never set to the live NFL week, so every hand-written blurb claimed week 1
  // regardless of when it was written — which made the manual-protection rule
  // in publishEspnBlurbs (now created_at based) never fire. Inserts send
  // week: null and the server stamps nothing.
  const [search, setSearch] = useState('')
  const [injuryFilter, setInjuryFilter] = useState('all')
  // 'default' preserves the server ordering (points-based). 'recent' sorts
  // by most recent published blurb, with never-blurbed players sunk to the
  // bottom — used to catch stale entries fast (e.g. an old May blurb still
  // sitting on Caitlin Clark's row in July).
  const [sortBy, setSortBy] = useState('default')
  const season = new Date().getFullYear()

  const filteredPlayers = (() => {
    let list = players
    const q = search.trim().toLowerCase()
    if (q) list = list.filter((p) => p.full_name?.toLowerCase().includes(q))
    if (injuryFilter === 'any') {
      list = list.filter((p) => !!p.injury_status)
    } else if (injuryFilter !== 'all') {
      list = list.filter((p) => p.injury_status === injuryFilter)
    }
    if (sortBy === 'recent') {
      list = [...list].sort((a, b) => {
        const at = a.last_published_at ? new Date(a.last_published_at).getTime() : null
        const bt = b.last_published_at ? new Date(b.last_published_at).getTime() : null
        // Nulls (no published blurb) sink to the bottom
        if (at == null && bt == null) return 0
        if (at == null) return 1
        if (bt == null) return -1
        return bt - at
      })
    }
    return list
  })()

  function formatPublishedAgo(iso) {
    if (!iso) return null
    const seconds = Math.floor((Date.now() - new Date(iso).getTime()) / 1000)
    if (seconds < 60) return 'just now'
    if (seconds < 3600) return `${Math.floor(seconds / 60)}m ago`
    if (seconds < 86400) return `${Math.floor(seconds / 3600)}h ago`
    if (seconds < 86400 * 7) return `${Math.floor(seconds / 86400)}d ago`
    if (seconds < 86400 * 30) return `${Math.floor(seconds / (86400 * 7))}w ago`
    if (seconds < 86400 * 365) return `${Math.floor(seconds / (86400 * 30))}mo ago`
    return `${Math.floor(seconds / (86400 * 365))}y ago`
  }

  const fetchPlayers = useCallback(async () => {
    setLoading(true)
    try {
      const data = await api.get(`/blurbs/players?season=${season}&position=${position}&sport=${sport}`)
      setPlayers(data)
    } catch (err) {
      toast(err.message, 'error')
    }
    setLoading(false)
  }, [position, season, sport])

  useEffect(() => { fetchPlayers() }, [fetchPlayers])

  const toggleSelect = (id) => {
    setSelected((prev) => {
      const next = new Set(prev)
      next.has(id) ? next.delete(id) : next.add(id)
      return next
    })
  }

  const selectAll = () => {
    // Acts on the currently visible (filtered) set — toggles on/off based on
    // whether every visible player is already selected.
    const allVisibleSelected = filteredPlayers.length > 0
      && filteredPlayers.every((p) => selected.has(p.id))
    setSelected((prev) => {
      const next = new Set(prev)
      if (allVisibleSelected) {
        for (const p of filteredPlayers) next.delete(p.id)
      } else {
        for (const p of filteredPlayers) next.add(p.id)
      }
      return next
    })
  }

  // ESPN blurbs are a FALLBACK, published per player on request. The server
  // refuses to overwrite anything hand-written and tells us which players it
  // skipped, so the toast reports what actually happened instead of a flat
  // count that would hide the skips.
  // Pull ESPN's latest injury notes for this sport into the draft queue.
  //
  // NFL is excluded because syncInjuries already does this every 5 minutes as
  // part of the injury_status patching fantasy scoring depends on — there is
  // nothing to trigger by hand. NBA/WNBA/MLB ride an hourly job, so this is
  // the "don't wait an hour" button.
  //
  // The server returns immediately and finishes in the background (MLB is
  // ~280 players, each a read-archive-insert), so this waits before
  // re-fetching rather than reporting a count it cannot know yet.
  const handleSyncEspn = async () => {
    setSyncingEspn(true)
    try {
      await api.post('/blurbs/sync-espn', { sport })
      toast(`Fetching ESPN ${sport.toUpperCase()} notes — this takes a moment`, 'success')
      setTimeout(fetchPlayers, 6000)
    } catch (err) {
      toast(err.message, 'error')
    } finally {
      setSyncingEspn(false)
    }
  }

  const handlePublishEspn = async () => {
    if (!selected.size) return toast('Select players first', 'error')
    setPublishingEspn(true)
    try {
      const r = await api.post('/blurbs/publish-espn', { playerIds: [...selected], sport })
      const parts = [`Published ${r.published}`]
      // Skips now mean "yours was written more recently than ESPN's draft",
      // not merely "you wrote something once" — worth saying, since the old
      // wording read as a refusal when it is really "yours is still current".
      if (r.skippedManual?.length) parts.push(`${r.skippedManual.length} yours already current`)
      if (r.noDraft?.length) parts.push(`${r.noDraft.length} had none`)
      toast(parts.join(' · '), r.published ? 'success' : 'error')
      setSelected(new Set())
      fetchPlayers()
    } catch (err) {
      toast(err.message, 'error')
    }
    setPublishingEspn(false)
  }

  const handleGenerate = async () => {
    if (!selected.size) return toast('Select players first', 'error')
    setGenerating(true)
    try {
      const result = await api.post('/blurbs/generate', {
        playerIds: [...selected],
        season,
        week: null,
      })
      toast(`Generated ${result.generated} blurbs`, 'success')
      setSelected(new Set())
      fetchPlayers()
    } catch (err) {
      toast(err.message, 'error')
    }
    setGenerating(false)
  }

  const handlePublish = async (blurbId) => {
    try {
      await api.post(`/blurbs/${blurbId}/publish`)
      toast('Published', 'success')
      fetchPlayers()
    } catch (err) {
      toast(err.message, 'error')
    }
  }

  const handlePublishAll = async () => {
    setPublishing(true)
    try {
      const result = await api.post('/blurbs/publish-all')
      toast(`Published ${result.published} blurbs`, 'success')
      fetchPlayers()
    } catch (err) {
      toast(err.message, 'error')
    }
    setPublishing(false)
  }

  const handleEdit = async (blurbId) => {
    try {
      await api.patch(`/blurbs/${blurbId}`, { content: editContent })
      toast('Updated', 'success')
      setEditingId(null)
      fetchPlayers()
    } catch (err) {
      toast(err.message, 'error')
    }
  }

  const handleCreate = async (playerId) => {
    try {
      await api.post('/blurbs', { player_id: playerId, content: createContent, season, week: null, sport })
      toast('Blurb created', 'success')
      setCreatingFor(null)
      setCreateContent('')
      fetchPlayers()
    } catch (err) {
      toast(err.message, 'error')
    }
  }

  const handleCreateAndPublish = async (playerId) => {
    try {
      const created = await api.post('/blurbs', { player_id: playerId, content: createContent, season, week: null, sport })
      await api.post(`/blurbs/${created.id}/publish`)
      toast('Published', 'success')
      setCreatingFor(null)
      setCreateContent('')
      fetchPlayers()
    } catch (err) {
      toast(err.message, 'error')
    }
  }

  const handleDelete = async (blurbId) => {
    if (!confirm('Delete this blurb?')) return
    try {
      await api.delete(`/blurbs/${blurbId}`)
      toast('Deleted', 'success')
      fetchPlayers()
      if (historyPlayerId) loadHistory(historyPlayerId)
    } catch (err) {
      toast(err.message, 'error')
    }
  }

  const loadHistory = async (playerId) => {
    if (historyPlayerId === playerId) { setHistoryPlayerId(null); return }
    setHistoryPlayerId(playerId)
    try {
      const data = await api.get(`/blurbs/player/${playerId}/history`)
      setHistory(data)
    } catch (err) {
      toast(err.message, 'error')
    }
  }

  const draftCount = players.filter((p) => p.blurb?.status === 'draft').length

  const positionOptions = POSITIONS_BY_SPORT[sport] || ['all']

  return (
    <div className="space-y-4">
      {/* Sport selector */}
      <div className="flex flex-wrap gap-1">
        {SPORTS.map((s) => (
          <button
            key={s.key}
            onClick={() => { setSport(s.key); setPosition('all'); setSelected(new Set()) }}
            className={`px-3 py-1.5 rounded-lg text-xs font-semibold transition-colors ${
              sport === s.key ? 'bg-accent text-white' : 'bg-bg-card text-text-secondary'
            }`}
          >
            {s.label}
          </button>
        ))}
      </div>

      {/* Controls */}
      <div className="flex flex-wrap items-center gap-3">
        <div className="flex gap-1 flex-wrap">
          {positionOptions.map((pos) => (
            <button
              key={pos}
              onClick={() => setPosition(pos)}
              className={`px-3 py-1.5 rounded-lg text-xs font-semibold transition-colors ${
                position === pos ? 'bg-accent text-white' : 'bg-bg-card text-text-secondary'
              }`}
            >
              {pos === 'all' ? 'All' : pos}
            </button>
          ))}
        </div>

        <input
          type="text"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Search players…"
          className="flex-1 min-w-[12rem] max-w-sm px-3 py-1.5 rounded-lg bg-bg-card border border-text-primary/20 text-sm text-text-primary placeholder-text-muted"
        />

        {/* Sort control — 'default' is the server's points-based order.
            'recent' surfaces the freshest blurbs first (never-blurbed at
            bottom) so stale entries like an old May blurb still sitting
            in July are easy to catch. */}
        <select
          value={sortBy}
          onChange={(e) => setSortBy(e.target.value)}
          className="px-3 py-1.5 rounded-lg bg-bg-card border border-text-primary/20 text-sm text-text-primary"
        >
          <option value="default">Sort: Default</option>
          <option value="recent">Sort: Recent blurb</option>
        </select>

      </div>

      {/* Injury status filter */}
      <div className="flex flex-wrap gap-1">
        {INJURY_FILTERS.map((f) => {
          const active = injuryFilter === f.key
          // Active styling must use literal Tailwind classes — dynamic
          // bg-${tone} strings get tree-shaken by the JIT compiler.
          const activeClass = !active ? 'bg-bg-card text-text-secondary border border-transparent'
            : f.key === 'Out' || f.key === 'IR' ? 'bg-incorrect/30 text-incorrect border border-incorrect/50'
            : f.key === 'Questionable' || f.key === 'Doubtful' || f.key === 'Day-To-Day' ? 'bg-yellow-500/30 text-yellow-500 border border-yellow-500/50'
            : f.key === 'Probable' ? 'bg-correct/30 text-correct border border-correct/50'
            : 'bg-accent text-white border border-transparent'
          return (
            <button
              key={f.key}
              onClick={() => setInjuryFilter(f.key)}
              className={`px-2.5 py-1 rounded-lg text-[11px] font-semibold transition-colors ${activeClass}`}
            >
              {f.label}
            </button>
          )
        })}
      </div>

      {/* Action buttons */}
      <div className="flex flex-wrap gap-2">
        <button
          onClick={selectAll}
          className="px-3 py-1.5 rounded-lg text-xs font-semibold bg-bg-card border border-text-primary/20 text-text-secondary hover:bg-bg-card/80"
        >
          {filteredPlayers.length > 0 && filteredPlayers.every((p) => selected.has(p.id)) ? 'Deselect All' : 'Select All'}
        </button>
        {sport === 'nfl' && (
          <button
            onClick={handleGenerate}
            disabled={generating || !selected.size}
            className="px-4 py-1.5 rounded-lg text-xs font-semibold bg-accent text-white disabled:opacity-50"
          >
            {generating ? 'Generating...' : `Generate AI Blurbs (${selected.size})`}
          </button>
        )}
        {sport !== 'nfl' && (
          <button
            onClick={handleSyncEspn}
            disabled={syncingEspn}
            title="Pull ESPN's latest injury notes for this sport into the draft queue. Runs hourly on its own; this is the manual nudge."
            className="px-4 py-1.5 rounded-lg text-xs font-semibold bg-bg-secondary text-text-primary border border-text-primary/20 hover:bg-text-primary/10 transition-colors disabled:opacity-50"
          >
            {syncingEspn ? 'Fetching...' : `Fetch ESPN ${sport.toUpperCase()} Notes`}
          </button>
        )}
        {/* Every sport, not just NFL — publishEspnBlurbs has always taken a
            sport and the route has always forwarded it; the button was the
            only thing hiding it. */}
        {(
          <button
            onClick={handlePublishEspn}
            disabled={publishingEspn || !selected.size}
            title="Publish ESPN's write-up for the selected players. Players you've already written about are left alone."
            className="px-4 py-1.5 rounded-lg text-xs font-semibold bg-bg-secondary text-text-primary border border-text-primary/20 hover:bg-text-primary/10 transition-colors disabled:opacity-50"
          >
            {publishingEspn ? 'Publishing...' : `Publish ESPN Blurbs (${selected.size})`}
          </button>
        )}
        {draftCount > 0 && (
          <button
            onClick={handlePublishAll}
            disabled={publishing}
            className="px-4 py-1.5 rounded-lg text-xs font-semibold bg-correct text-white disabled:opacity-50"
          >
            {publishing ? 'Publishing...' : `Publish All Drafts (${draftCount})`}
          </button>
        )}
      </div>

      {/* Player list */}
      {loading ? (
        <LoadingSpinner />
      ) : filteredPlayers.length === 0 ? (
        <p className="text-sm text-text-muted text-center py-8">
          {search.trim()
            ? `No players matching "${search}"`
            : injuryFilter !== 'all'
              ? `No players with ${injuryFilter === 'any' ? 'any injury' : injuryFilter} status`
              : 'No players found'}
        </p>
      ) : (
        <div className="space-y-1">
          {filteredPlayers.map((player) => {
            const isSelected = selected.has(player.id)
            const blurb = player.blurb
            const isEditing = editingId === blurb?.id
            const isCreating = creatingFor === player.id
            const showingHistory = historyPlayerId === player.id
            const isExpanded = expandedId === player.id
            // Only offer tap-to-expand when there's actually a blurb to
            // preview. No blurb → nothing useful to reveal.
            const canExpand = !!blurb?.content

            return (
              <div key={player.id} className="bg-bg-primary border border-text-primary/20 rounded-xl overflow-hidden">
                {/* Player row */}
                <div className="flex items-center gap-3 px-3 py-2.5">
                  <input
                    type="checkbox"
                    checked={isSelected}
                    onChange={() => toggleSelect(player.id)}
                    className="shrink-0 accent-accent"
                  />
                  <div
                    className={`flex-1 min-w-0 ${canExpand ? 'cursor-pointer' : ''}`}
                    onClick={canExpand ? () => setExpandedId(isExpanded ? null : player.id) : undefined}
                  >
                    <div className="flex items-center gap-2">
                      <span className="text-sm font-semibold text-text-primary truncate">{player.full_name}</span>
                      <span className="text-[10px] text-text-muted">{player.position} · {player.team}</span>
                      {player.injury_status && (
                        <span className={`text-[10px] font-bold px-1 py-0.5 rounded ${
                          player.injury_status === 'Out' ? 'bg-incorrect/20 text-incorrect'
                          : player.injury_status === 'Probable' ? 'bg-correct/20 text-correct'
                          : 'bg-yellow-500/20 text-yellow-500'
                        }`}>{player.injury_status}</span>
                      )}
                    </div>
                    {sport === 'nfl' && (
                      <div className="text-[10px] text-text-muted">
                        {player.seasonPoints} pts · {player.gamesPlayed} GP · {player.avgPoints} avg
                      </div>
                    )}
                    {/* Last-published freshness — actual date rendered on
                        the row so stale entries jump out during scanning
                        (e.g. an old May blurb still riding into July).
                        Bumped from [10px] muted → xs semibold-secondary so
                        the date reads as a first-class piece of info, not
                        a whisper. */}
                    {player.last_published_at && (
                      <div className="text-xs text-text-secondary mt-1" title={new Date(player.last_published_at).toLocaleString()}>
                        Published <span className="font-semibold text-text-primary">{new Date(player.last_published_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}</span>
                        <span className="text-text-muted"> · {formatPublishedAgo(player.last_published_at)}</span>
                      </div>
                    )}
                    {/* Mobile: drop Published/Draft badge under the name row so
                        it doesn't compete for horizontal space with the
                        injury badge. Desktop still shows it in the right
                        column with Edit. */}
                    {blurb && (
                      <span className={`sm:hidden inline-block text-[10px] font-bold px-1.5 py-0.5 rounded mt-1 ${
                        blurb.status === 'published' ? 'bg-correct/20 text-correct' : blurb.generated_by === 'espn' ? 'bg-blue-500/15 text-blue-400' : 'bg-yellow-500/20 text-yellow-500'
                      }`}>
                        {blurb.status === 'published' ? 'Published' : blurb.generated_by === 'espn' ? 'ESPN Draft' : 'Draft'}
                      </span>
                    )}
                  </div>

                  {/* Blurb status indicator */}
                  <div className="flex items-center gap-1.5 shrink-0">
                    {blurb && (
                      <>
                        <span className={`hidden sm:inline-block text-[10px] font-bold px-1.5 py-0.5 rounded ${
                          blurb.status === 'published' ? 'bg-correct/20 text-correct' : blurb.generated_by === 'espn' ? 'bg-blue-500/15 text-blue-400' : 'bg-yellow-500/20 text-yellow-500'
                        }`}>
                          {blurb.status === 'published' ? 'Published' : blurb.generated_by === 'espn' ? 'ESPN Draft' : 'Draft'}
                        </span>
                        <button
                          onClick={() => { setEditingId(isEditing ? null : blurb.id); setEditContent(blurb.content) }}
                          className="text-accent text-xs hover:underline"
                        >
                          Edit
                        </button>
                        {blurb.status === 'draft' && (
                          <button onClick={() => handlePublish(blurb.id)} className="text-correct text-xs hover:underline">
                            Publish
                          </button>
                        )}
                      </>
                    )}
                    {/* WHO WROTE THE LIVE BLURB. Distinct from the "waiting"
                        chip below: once an ESPN draft is published there is no
                        pending draft left, so that chip disappears and the row
                        read a bare green "Published" with nothing saying the
                        text was ESPN's. Bijan Robinson's row looked identical
                        to a hand-written one. */}
                    {blurb?.generated_by === 'espn' && blurb.status === 'published' && (
                      <span
                        title="This live blurb was written by ESPN, not by you"
                        className="text-[10px] font-bold px-1.5 py-0.5 rounded bg-blue-500/15 text-blue-400"
                      >
                        ESPN
                      </span>
                    )}
                    {/* An ESPN write-up is QUEUED for this player. Shown as a
                        marker rather than taking the row over: the row belongs
                        to YOUR blurb, and Edit must open yours. Only rendered
                        when it isn't already the thing on display, so players
                        you've never written about don't get a redundant tag.
                        Worded distinctly from the authorship chip above — both
                        can appear at once when ESPN's published text has been
                        superseded by a newer ESPN draft. */}
                    {player.espn_draft && blurb?.id !== player.espn_draft.id && (
                      <span
                        title="ESPN has a NEWER write-up waiting — expand the row to read it"
                        className="text-[10px] font-bold px-1.5 py-0.5 rounded bg-blue-500/15 text-blue-400 ring-1 ring-blue-400/40"
                      >
                        ESPN waiting
                      </span>
                    )}
                    {/* + always present so admins can publish a new blurb on top
                        of an existing one. Old blurbs stay in history; the
                        newest becomes current Player Notes. */}
                    <button
                      onClick={() => { setCreatingFor(isCreating ? null : player.id); setCreateContent('') }}
                      className="text-accent text-lg leading-none hover:text-accent/80"
                      title="Add new blurb"
                    >+</button>
                    <button
                      onClick={() => loadHistory(player.id)}
                      className="text-text-muted text-xs hover:text-text-primary"
                      title="View history"
                    >
                      <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                        <path strokeLinecap="round" strokeLinejoin="round" d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                      </svg>
                    </button>
                  </div>
                </div>

                {/* Tap-to-preview the current blurb. Read-only — hides
                    when Edit is opened to avoid rendering the same
                    content twice. */}
                {isExpanded && !isEditing && (blurb?.content || player.espn_draft) && (
                  <div className="px-3 pb-3 space-y-2">
                    {blurb?.content && (
                      <div className="rounded-lg bg-bg-card border border-text-primary/10 px-3 py-2 text-sm text-text-primary whitespace-pre-wrap">
                        {blurb.content}
                      </div>
                    )}
                    {/* ESPN's queued version sits BELOW yours, never replacing
                        it in the row. Publishing it is an explicit per-player
                        choice: publishBlurb archives whatever is currently
                        live, so ESPN's takes over only when you say so. The
                        bulk action still skips players you've written about —
                        this button is the deliberate override. */}
                    {player.espn_draft && blurb?.id !== player.espn_draft.id && (
                      <div className="rounded-lg bg-bg-card border border-blue-500/30 px-3 py-2">
                        <div className="flex items-baseline justify-between gap-3 mb-1">
                          <span className="text-[10px] font-bold uppercase tracking-wider text-blue-400">
                            Waiting from ESPN
                          </span>
                          <button
                            onClick={() => handlePublish(player.espn_draft.id)}
                            className="text-correct text-xs font-semibold hover:underline shrink-0"
                          >
                            {blurb?.status === 'published' ? 'Publish instead' : 'Publish'}
                          </button>
                        </div>
                        <div className="text-sm text-text-secondary whitespace-pre-wrap leading-relaxed">
                          {player.espn_draft.content}
                        </div>
                      </div>
                    )}
                  </div>
                )}

                {/* Edit inline */}
                {isEditing && (
                  <div className="px-3 pb-3 space-y-2">
                    <textarea
                      value={editContent}
                      onChange={(e) => setEditContent(e.target.value)}
                      rows={3}
                      className="w-full px-3 py-2 rounded-lg bg-bg-card border border-text-primary/20 text-sm text-text-primary resize-none"
                    />
                    <div className="flex gap-2">
                      <button onClick={() => handleEdit(blurb.id)} className="px-3 py-1 rounded-lg text-xs font-semibold bg-accent text-white">Save</button>
                      <button onClick={() => handleDelete(blurb.id)} className="px-3 py-1 rounded-lg text-xs font-semibold bg-incorrect/20 text-incorrect">Delete</button>
                      <button onClick={() => setEditingId(null)} className="px-3 py-1 rounded-lg text-xs font-semibold text-text-muted">Cancel</button>
                    </div>
                  </div>
                )}

                {/* Create inline */}
                {isCreating && (
                  <div className="px-3 pb-3 space-y-2">
                    <textarea
                      value={createContent}
                      onChange={(e) => setCreateContent(e.target.value)}
                      rows={3}
                      placeholder="Write a player note..."
                      className="w-full px-3 py-2 rounded-lg bg-bg-card border border-text-primary/20 text-sm text-text-primary resize-none"
                    />
                    <div className="flex gap-2">
                      <button
                        onClick={() => handleCreate(player.id)}
                        disabled={!createContent.trim()}
                        className="px-3 py-1 rounded-lg text-xs font-semibold bg-accent text-white disabled:opacity-50"
                      >Create Draft</button>
                      <button
                        onClick={() => handleCreateAndPublish(player.id)}
                        disabled={!createContent.trim()}
                        className="px-3 py-1 rounded-lg text-xs font-semibold bg-correct text-white disabled:opacity-50"
                      >Publish</button>
                      <button onClick={() => setCreatingFor(null)} className="px-3 py-1 rounded-lg text-xs font-semibold text-text-muted">Cancel</button>
                    </div>
                  </div>
                )}

                {/* History dropdown */}
                {showingHistory && (
                  <div className="px-3 pb-3">
                    <div className="text-[10px] text-text-muted uppercase tracking-wider mb-1.5">Blurb History</div>
                    {history.length === 0 ? (
                      <div className="text-xs text-text-muted">No history</div>
                    ) : (
                      <div className="space-y-2 max-h-48 overflow-y-auto">
                        {history.map((h) => {
                          // Border color = source (manual / ai / espn). Status
                          // is conveyed via the colored status label + opacity
                          // dimming for archived rows.
                          const sourceBorder = h.generated_by === 'ai'
                            ? 'border-purple-500/60'
                            : h.generated_by === 'espn'
                              ? 'border-blue-500/60'
                              : 'border-accent/60'
                          const sourceLabel = h.generated_by === 'ai'
                            ? 'AI'
                            : h.generated_by === 'espn'
                              ? 'ESPN'
                              : 'Manual'
                          return (
                            <div key={h.id} className={`rounded-lg border p-2 text-xs ${sourceBorder} ${h.status === 'archived' ? 'opacity-60' : ''}`}>
                              <div className="flex items-center justify-between mb-1">
                                <span className={`font-bold ${
                                  h.status === 'published' ? 'text-correct' : h.status === 'draft' ? 'text-yellow-500' : 'text-text-muted'
                                }`}>{h.status}</span>
                                <div className="flex items-center gap-2">
                                  <span className="text-text-muted">
                                    {sourceLabel}
                                    {h.writer && ` · by ${h.writer.display_name || h.writer.username}`}
                                    {h.published_at && ` · ${new Date(h.published_at).toLocaleDateString()}`}
                                  </span>
                                  <button onClick={() => handleDelete(h.id)} className="text-incorrect hover:underline">Delete</button>
                                </div>
                              </div>
                              <p className="text-text-primary">{h.content}</p>
                            </div>
                          )
                        })}
                      </div>
                    )}
                  </div>
                )}
              </div>
            )
          })}
        </div>
      )}
    </div>
  )
}
